/*
 * Copyright (c) 2026-present Amida Technology Solutions, Inc.
 * All rights reserved.
 *
 * Licensed under the Amida proprietary license.
 * See LICENSE file in the project root for license information.
 */

import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query';
import { z } from 'zod';

import { request, requestFile } from '@/api/client';
import { workflowFieldSchema } from '@/api/fields';
import { formatDate } from '@/lib/dates';
import { REASON_LEAST, REASON_MOST, reasonProblem } from '@/lib/reason';
import type { operations } from '@/types/api';

export const STAGES = [
  'draft',
  'in_approval',
  // Approved and not yet sent. It was missing from this list, so an agreement
  // sitting there could be reached only by clearing every filter — and the
  // dashboard, which counts it, had nowhere to send anybody.
  'ready_to_send',
  'out_for_signature',
  'executed',
  'filed',
] as const;

export type Stage = (typeof STAGES)[number];

/**
 * How each stage reads on screen, and how urgent it looks.
 *
 * `filed` reads as **Completed**, because that is what it means to the person
 * looking: everybody has signed and the document is safely in the store, and
 * there is nothing left for anyone to do. "Filed" describes the last mechanical
 * step rather than the state, and left people asking what was still outstanding.
 *
 * The stage itself is still `filed` everywhere it matters — in the database, in
 * the audit trail, in the rule that says filing is all-or-nothing. Only the word
 * on the screen changes.
 */
export const STAGE_LABEL: Record<string, string> = {
  draft: 'Draft',
  in_approval: 'In approval',
  ready_to_send: 'Ready to send',
  out_for_signature: 'Out for signature',
  executed: 'Signed',
  filed: 'Completed',
  superseded: 'Superseded',
  cancelled: 'Cancelled',
};

export const agreementSchema = z.object({
  id: z.string(),
  reference: z.string(),
  title: z.string(),
  stage: z.string(),
  workflow_name: z.string(),
  counterparty_name: z.string(),
  owner_name: z.string(),
  owner_initials: z.string(),
  effective_date: z.string().nullable(),
  expires_at: z.string().nullable(),
  fiscal_quarter: z.string().nullable(),
  /** When the last signature landed, as the provider said, where it is known. */
  signed_date: z.string().nullable().default(null),
  /**
   * Whether the signed copy is filed, so there is a signed document to open.
   * False between the last signature and the filing, and for history
   * imported without its file: Records says so rather than offering to open
   * a draft that was never signed.
   */
  signed_copy: z.boolean().default(false),
  /** Whether it is a step of a packet, so Records can say so (`PKT-25`). */
  in_packet: z.boolean().default(false),
  /**
   * Whether it was signed outside Vellum and imported -- from a spreadsheet,
   * or as a signed PDF -- who imported it and on what day (`IMP-24`). The
   * screens say so, and show no signature event Vellum never saw. A server
   * that predates the question does not send it, and the answer then is no.
   */
  imported: z.boolean().default(false),
  imported_by: z.string().nullable().default(null),
  imported_on: z.string().nullable().default(null),
  /** When it was started. A server that predates the question does not say. */
  created_at: z.string().nullable().default(null),
  /**
   * Whether you may delete it now: an Admin's, and only what never went out
   * for signature -- before sending, or cancelled before it was sent and not
   * waited on by an open packet. For the screen only: the server decides.
   */
  can_delete: z.boolean().default(false),
});

export type Agreement = z.infer<typeof agreementSchema>;

/** A step's default copy: where its signed copy usually goes (`PKT-20`). */
export const defaultCopySchema = z.object({
  to: z.array(z.string()),
  subject: z.string().nullable(),
  body: z.string().nullable(),
});

export type DefaultCopy = z.infer<typeof defaultCopySchema>;

/**
 * The packet an agreement is a step of (`PKT-25`, `PKT-26`). A step the
 * reader may not open comes with its title alone: no stage, no id.
 */
export const agreementPacketSchema = z.object({
  packet_id: z.string(),
  reference: z.string(),
  name: z.string(),
  subject_name: z.string(),
  step_order: z.number(),
  total: z.number(),
  can_open: z.boolean(),
  can_send_copy: z.boolean(),
  default_copy: defaultCopySchema.nullable(),
  /** For the starter, once this step is signed: the step to start next. */
  next_step: z
    .object({ step_order: z.number(), workflow_id: z.string(), title: z.string() })
    .nullable(),
  steps: z.array(
    z.object({
      step_order: z.number(),
      title: z.string(),
      stage: z.string().nullable(),
      agreement_id: z.string().nullable(),
      started: z.boolean(),
    }),
  ),
});

export type AgreementPacket = z.infer<typeof agreementPacketSchema>;

/**
 * How an imported agreement says where it was signed, or `null` for one
 * Vellum sent. "Signed outside Vellum, imported by Priya Raman on 29 Sep
 * 2026", with whatever part of that is known.
 */
export function importedLabel(
  agreement: Pick<Agreement, 'imported' | 'imported_by' | 'imported_on'>,
): string | null {
  if (!agreement.imported) return null;
  const by = agreement.imported_by ? ` by ${agreement.imported_by}` : '';
  const on = agreement.imported_on ? ` on ${formatDate(agreement.imported_on)}` : '';
  return `Signed outside Vellum, imported${by}${on}`;
}

export const agreementPageSchema = z.object({
  items: z.array(agreementSchema),
  total: z.number(),
  limit: z.number(),
  offset: z.number(),
  has_more: z.boolean(),
});

export const approverSchema = z.object({
  /**
   * The row's own id. An approver is addressed by it -- never by their place
   * in the order, which two can share.
   */
  id: z.string(),
  name: z.string(),
  initials: z.string(),
  decision: z.string().nullable(),
  sort_order: z.number(),
  /** Whether this is the person reading, so the screen can explain itself. */
  is_me: z.boolean(),
  /**
   * Whether the person reading may ask somebody else to approve in this one's
   * place, by exactly the rule the server applies. Offering the action is the
   * screen's; allowing it is the server's (`API-9`). A server that predates
   * the question does not send it, and the answer then is no.
   */
  can_replace: z.boolean().default(false),
  /**
   * Whether the person reading may resend or remind now, by the server's own
   * rule: the owner or an Admin, while it waits on this person, and not
   * inside the hour or past five today (`REM-2`, `REM-5`). For the screen
   * only; the server decides again. A server that predates the question does
   * not send it, and the answer then is no.
   */
  can_remind: z.boolean().default(false),
  /** When they were last sent again or reminded, manual or automatic (`REM-15`). */
  last_reminded_at: z.string().nullable().default(null),
  /** Who did it; null with a time means Vellum did, automatically. */
  last_reminded_by: z.string().nullable().default(null),
  /** When the next is allowed, where one is not now because of `REM-5`. */
  next_reminder_at: z.string().nullable().default(null),
  /** Whom they approve for, when out of office moved it to them; else null. */
  covering_for: z.string().nullable().default(null),
  /**
   * Whether it is this approver's turn: approvers are asked one after
   * another, in the template's order (`LC-06`). False outside approval, and
   * from a server that predates the order.
   */
  is_current: z.boolean().default(false),
});

export type Approver = z.infer<typeof approverSchema>;

/**
 * Why somebody else was asked to approve. A fixed list: free text about why a
 * person cannot act fills up with things nobody should be writing down.
 */
export const REASON_CODES = [
  'on_leave',
  'left_amida',
  'wrong_person',
  'counterparty_asked',
  'other',
] as const;

export type ReasonCode = (typeof REASON_CODES)[number];

export const REASON_LABEL: Record<ReasonCode, string> = {
  on_leave: 'On leave',
  left_amida: 'Left Amida',
  wrong_person: 'The wrong person was named',
  counterparty_asked: 'The counterparty asked',
  other: 'Something else',
};

/**
 * Somebody asked to approve in another's place, and why. The note is shown
 * here, to people who can open the agreement, and goes nowhere else -- never
 * a notification, a log line or the audit trail.
 */
export const approverChangeSchema = z.object({
  id: z.string(),
  sort_order: z.number(),
  replaced_name: z.string(),
  replaced_initials: z.string(),
  name: z.string(),
  initials: z.string(),
  reason: z.enum(REASON_CODES),
  note: z.string().nullable(),
  changed_by_name: z.string(),
  changed_at: z.string(),
});

export type ApproverChange = z.infer<typeof approverChangeSchema>;

/**
 * Where a withdrawal of a signature request got to. `requested` waits on
 * Dropbox Sign; the rest are settled by Vellum reading its record.
 */
export const WITHDRAWAL_STATES = [
  'requested',
  'withdrawn',
  'overtaken',
  'refused',
  'failed',
] as const;

/**
 * One withdrawal of one of the agreement's signature requests. The note is
 * shown here, to people who can open the agreement, and goes nowhere else.
 * A request cancelled on Dropbox Sign's own screens, with nobody in Vellum
 * asking, has no reason, no note and nobody who asked.
 */
export const withdrawalSchema = z.object({
  id: z.string(),
  state: z.enum(WITHDRAWAL_STATES),
  reason: z.enum(REASON_CODES).nullable(),
  note: z.string().nullable(),
  requested_by_name: z.string().nullable(),
  requested_at: z.string().nullable(),
  settled_at: z.string().nullable(),
});

export type Withdrawal = z.infer<typeof withdrawalSchema>;

/**
 * One signature spot: a character inside the document's own words.
 *
 * `block` is the paragraph or list, `item` the line of a list (null in a
 * paragraph), `run` the stretch of words within it and `offset` the character
 * in that stretch where the box goes. Every number counts from the document
 * the workflow published -- never from a rendering with spots already drawn
 * in, which would renumber everything after the first. The preview's markup
 * carries the same numbers, so a click is read straight back into one of
 * these.
 *
 * `party` is the document's word for who signs (`company`), not the signing
 * provider's (`amida`): the server maps one to the other in one place.
 *
 * `kind` is what goes at that character: the party's signature, or a field
 * their signer fills in beside it -- a name, the date, a title, an address,
 * or a field the person launching it names (`text`, whose `label` is that
 * name). Every other kind is named for what it is and carries no label. A
 * spot recorded before there were fields has no kind, and was a signature:
 * the defaults say so, for a server that predates the question as for one
 * that answers it.
 */
export const PLACEMENT_KINDS = [
  'signature',
  'name',
  'date',
  'title',
  'address',
  'text',
] as const;

/**
 * What a signer may type into a field, which Dropbox Sign checks on the
 * signing page as they type: anything, letters only, numbers only, an email
 * address or a phone number. Only a field the signer types into takes one;
 * a signature and a date are always `any`. The server says which applies to
 * every spot, a name's letters included, so nothing here has to know the
 * defaults to read one; a spot from before there was a choice took anything.
 */
export const ALLOWED_INPUTS = ['any', 'letters', 'numbers', 'email', 'phone'] as const;

export const placementSchema = z.object({
  party: z.enum(['company', 'counterparty']),
  block: z.number(),
  item: z.number().nullable(),
  /** In a table: the row and the cell in it, from `data-row` and `data-cell`. */
  row: z.number().nullable().default(null),
  cell: z.number().nullable().default(null),
  run: z.number(),
  offset: z.number(),
  kind: z.enum(PLACEMENT_KINDS).default('signature'),
  label: z.string().nullable().default(null),
  allowed: z.enum(ALLOWED_INPUTS).default('any'),
  /**
   * Which of the side's signers it is for, from one (templates spec §18,
   * `WF-98`). Left out, slot 1 (D-55): every spot from before slots is slot
   * 1's, and the screens leave it out for slot 1 too. The server always says;
   * a null from one that predates slots reads as left out.
   */
  slot: z.number().nullable().exactOptional(),
});

export type Placement = z.infer<typeof placementSchema>;

/**
 * A line the document says something is filled in on, whose party its
 * heading does not say: a placement without its party. The screen gives it
 * to who signs, marked assumed (`assumeSides`); the server never guesses.
 */
export const unsureSchema = z.object({
  block: z.number(),
  item: z.number().nullable(),
  row: z.number().nullable(),
  cell: z.number().nullable(),
  run: z.number(),
  offset: z.number(),
  kind: z.enum(PLACEMENT_KINDS),
});

export type Unsure = z.infer<typeof unsureSchema>;

/**
 * What "Place them for me" offers: a spot on every labelled signature line
 * the document has -- Signature, Name, Printed Name, Title, Date -- for the
 * side its heading names, and apart the lines whose side it does not name.
 * Proposals: nothing is placed until the button is pressed, nothing saved
 * until the person saves. A server that predates it sends none.
 */
export const foundSchema = z.object({
  placements: z.array(placementSchema),
  unsure: z.array(unsureSchema),
});

export type FoundSpots = z.infer<typeof foundSchema>;

/** Nothing found: a preview from a server that predates "Place them for me". */
export const NOTHING_FOUND: FoundSpots = { placements: [], unsure: [] };
export type PlacementParty = Placement['party'];

/**
 * Spots as they are sent: slot 1 left out, as every spot from before slots
 * was (D-55), and a null slot -- which the server never sends -- never sent
 * back, since the server takes a number or nothing.
 */
export const placementsOut = (placements: readonly Placement[]): Placement[] =>
  placements.map(({ slot, ...rest }) =>
    slot !== undefined && slot !== null && slot !== 1 ? { ...rest, slot } : rest,
  );
export type PlacementKind = Placement['kind'];
export type AllowedInput = Placement['allowed'];

// Without `in_packet`: the page carries the packet itself, in `packet`. Nor
// `can_delete`, a list's: the page decides from its stage and its request.
export const agreementDetailSchema = agreementSchema
  .omit({ in_packet: true, created_at: true, can_delete: true })
  .extend({
    description: z.string().nullable(),
    /** The id it had in Ironclad, for an imported one (`IMP-28`); null otherwise. */
    legacy_reference: z.string().nullable().default(null),
    /** Whether you started it. Nobody approves their own. */
    is_mine: z.boolean(),
    /**
     * Whether you run the packet it is a step of -- it was assigned to you, or
     * you assigned it on -- and so may change and send it as its owner may.
     * A server that predates the rule does not send it, and the answer is no.
     */
    runs_its_packet: z.boolean().default(false),
    /**
     * Whether you may change who signs it: the person who started it, or an
     * administrator (`LIFE-39`). For the screen to offer the form; the server
     * decides again. A server that predates the rule does not send it, and the
     * answer then is no.
     */
    can_change_signers: z.boolean().default(false),
    /**
     * Whether you may set when it expires, or that it does not: the person who
     * started it or an administrator, at any stage (VL-2041). For the screen
     * to offer Set expiry date; the server decides again. A server that
     * predates the rule does not send it, and the answer then is no.
     */
    can_set_expiry: z.boolean().default(false),
    /**
     * Whether you may withdraw its signature request now: the person who
     * started it or an administrator, while it is out for signature and
     * nothing waits on Dropbox Sign. The server decides again.
     */
    can_withdraw: z.boolean().default(false),
    /** Whether a withdrawal is waiting on Dropbox Sign: asked for, not yet gone. */
    withdrawal_pending: z.boolean().default(false),
    /** Every withdrawal of its signature requests, oldest first. */
    withdrawals: z.array(withdrawalSchema).default([]),
    /**
     * The provider its open signature request was sent through, where that is
     * not the one this deployment uses: `docusign`, for a request sent before
     * the move to Dropbox Sign (`ADR-0004`). Nothing reads that provider any
     * more, so the request cannot be withdrawn or changed from here, and nothing
     * waits on it -- it is finished or withdrawn there. Null otherwise, and for
     * a server that predates the question.
     */
    unread_signature_provider: z.string().nullable().default(null),
    requires_approval: z.boolean(),
    store: z.string().nullable(),
    filed: z.boolean(),
    /** Which parties the workflow asks to sign, and who is asked first. */
    company_signs: z.boolean(),
    counterparty_signs: z.boolean(),
    /** Who signs first: this agreement's own choice, else its template's (§19). */
    signing_order: z.enum(['counterparty_first', 'company_first']),
    /** Whether `signing_order` was chosen on this agreement rather than its template. */
    signing_order_is_own: z.boolean().default(false),
    /**
     * Whether you may change who signs first now: the person who started it or
     * an administrator, until it is sent (WF-115). UX only; the server decides.
     */
    can_change_signing_order: z.boolean().default(false),
    approvers: z.array(approverSchema),
    /**
     * Who was asked to approve in whose place, oldest first. Empty for nearly
     * everything, and for a server that predates the question.
     */
    approver_changes: z.array(approverChangeSchema).default([]),
    field_values: z.record(z.string(), z.string()),
    /**
     * What its own workflow version asks, in order. The agreement page edits
     * the values through these, as the launch form does; a server that
     * predates the question sends none, and then nothing is offered.
     */
    fields: z.array(workflowFieldSchema).default([]),
    /**
     * Where each side signs, as placed on the launch form. Null means the step
     * has not been done -- not "nowhere", which is not an answer the server
     * accepts.
     */
    signature_placements: z.array(placementSchema).nullable().default(null),
    /**
     * When the signature provider last answered about this agreement's
     * request, or null if it was never sent or never asked about. The page says
     * it, so "Out for signature" can be told apart from "the last thing we
     * heard". A server that predates the question does not send it.
     */
    last_checked_at: z.string().nullable().default(null),
    /**
     * Whether its latest signature request was a Dropbox Sign test send:
     * watermarked, and binding on nobody. The page marks it, so a test that
     * was signed and filed is never taken for an executed agreement. A server
     * that predates the question does not send it.
     */
    test_send: z.boolean().default(false),
    /**
     * Whether it was sent with the signature provider in use. Only then is
     * there anybody to wait on, or anything to ask: history imported already
     * signed has no request anywhere, and a page that waited on one would wait
     * for ever.
     */
    has_signature_request: z.boolean().default(false),
    /**
     * Whether a document has been made for it yet. Until one is, it cannot go
     * for approval (`LC-03`). A server that predates it does not send it, and
     * the answer then is "yes" -- the server still refuses on Send.
     */
    has_document: z.boolean().default(true),
    /** The packet it is a step of, or null. */
    packet: agreementPacketSchema.nullable().default(null),
    /**
     * Whether you may cancel it now: its owner, or an Admin, before it is
     * signed (`LIFE-64`). For the screen only; the server decides again. A
     * server that predates the question does not send it, and the answer
     * then is no.
     */
    can_cancel: z.boolean().default(false),
    /**
     * Whether a cancel waits on Dropbox Sign to confirm its request is
     * withdrawn (`LIFE-66`). Until then it is still out for signature.
     */
    cancel_pending: z.boolean().default(false),
    /**
     * Why it was cancelled, when, and by whom. Shown on its page and nowhere
     * else (`LIFE-65`). Null unless it was.
     */
    cancel_reason: z.string().nullable().default(null),
    cancelled_at: z.string().nullable().default(null),
    cancelled_by_name: z.string().nullable().default(null),
    /**
     * Whether you may email a copy of its signed document now: its owner or
     * an Admin, once it is filed (`LIFE-70`). For the screen only; the server
     * decides again. A server that predates the question does not send it,
     * and the answer then is no.
     */
    can_send_copy: z.boolean().default(false),
    /**
     * Whether you may read its document inside Vellum (View) and save it
     * (Download): its owner, a named approver, an Admin, or anybody with
     * access to its template (AC-01, `LIFE-74`). For the screen only; the
     * server decides again. A server that predates the question does not
     * send it, and the answer then is no.
     */
    can_view_document: z.boolean().default(false),
    can_download: z.boolean().default(false),
    /**
     * Whether you may add an obligation to it now: its owner, Operations or
     * an Admin, while it is neither cancelled nor superseded. For the screen
     * only; the server decides again. A server that predates the question
     * does not send it, and the answer then is no.
     */
    can_add_obligation: z.boolean().default(false),
    /**
     * Whose document it is on. `company` is Amida's own, generated from its
     * template; `counterparty` is a file the other side drafted, uploaded
     * when it was started, which Vellum never edits or generates -- and then
     * `counterparty_signs` is this agreement's own choice, not its
     * template's. A server that predates the question sends nothing, and
     * every agreement it knows of is on Amida's paper.
     */
    paper: z.enum(['company', 'counterparty']).default('company'),
  });

export type AgreementDetail = z.infer<typeof agreementDetailSchema>;

/**
 * What a list of agreements asks the server for.
 *
 * Every one of these is the server's to apply, over everything the person may
 * see -- never a page that happened to arrive, filtered afterwards, which
 * could not find anything past its last row.
 */
/** What the list endpoint takes, as the contract says it (`API-1`). */
type ListQuery = NonNullable<
  operations['list_agreements_api_v1_agreements_get']['parameters']['query']
>;

/** The columns the server sorts by. Anything else, it refuses. */
export type AgreementSort = NonNullable<ListQuery['sort']>;

/** The same list as a value, for the URL's validation. */
export const AGREEMENT_SORTS = [
  'reference',
  'title',
  'workflow',
  'counterparty',
  'stage',
  'owner',
  'created',
  'expires',
  'signed',
] as const satisfies readonly AgreementSort[];

/** A column's sort key as the server's, or none for a key it does not know. */
export function sortOf(key: string): AgreementSort | undefined {
  return AGREEMENT_SORTS.find((known) => known === key);
}

export interface AgreementFilters {
  stage?: string | undefined;
  /** The title, the reference or the counterparty's name. */
  search?: string | undefined;
  /** One workflow, by its name. */
  workflow?: string | undefined;
  /** One workflow, by its id. */
  workflowId?: string | undefined;
  /** One counterparty, by its id. */
  counterpartyId?: string | undefined;
  /** Who started it, by their id. */
  ownerId?: string | undefined;
  /** The team that owns its workflow, by the team's key. */
  team?: string | undefined;
  /** Started between these days, inclusive. ISO dates. */
  createdFrom?: string | undefined;
  createdTo?: string | undefined;
  /** Signed between these days, inclusive. ISO dates. */
  signedFrom?: string | undefined;
  signedTo?: string | undefined;
  /** The year it was signed in. */
  year?: number | undefined;
  /** True for packet steps only, false for agreements started on their own. */
  inPacket?: boolean | undefined;
  /** `signed` for the records, most recently signed first. */
  order?: 'created' | 'signed' | undefined;
  /** A column to sort by, and which way. Overrides `order`. */
  sort?: AgreementSort | undefined;
  direction?: 'asc' | 'desc' | undefined;
  /** How many a page holds, and where it starts. */
  limit?: number | undefined;
  offset?: number | undefined;
}

/** The query string for a set of filters: only what is set is sent. */
export function agreementParams(filters: AgreementFilters): URLSearchParams {
  const params = new URLSearchParams({ limit: String(filters.limit ?? 50) });
  const pairs: [string, string | number | undefined][] = [
    ['stage', filters.stage],
    ['search', filters.search],
    ['workflow', filters.workflow],
    ['workflow_id', filters.workflowId],
    ['counterparty_id', filters.counterpartyId],
    ['owner_id', filters.ownerId],
    ['team', filters.team],
    ['created_from', filters.createdFrom],
    ['created_to', filters.createdTo],
    ['signed_from', filters.signedFrom],
    ['signed_to', filters.signedTo],
    ['signed_year', filters.year],
    ['in_packet', filters.inPacket === undefined ? undefined : String(filters.inPacket)],
    ['order', filters.order],
    ['sort', filters.sort],
    ['direction', filters.direction],
    ['offset', filters.offset || undefined],
  ];
  for (const [key, value] of pairs) {
    if (value !== undefined && value !== '') params.set(key, String(value));
  }
  return params;
}

export const agreementsQuery = (filters: AgreementFilters = {}) =>
  queryOptions({
    queryKey: ['agreements', filters] as const,
    queryFn: ({ signal }) =>
      request(
        `/api/v1/agreements?${agreementParams(filters).toString()}`,
        agreementPageSchema,
        {
          signal,
        },
      ),
  });

export const agreementQuery = (id: string) =>
  queryOptions({
    queryKey: ['agreement', id] as const,
    // Marked a poll: the detail page asks again every thirty seconds while a
    // signature is outstanding, and that must not keep an idle session alive.
    // A person opening the page is already reported by the session guard.
    queryFn: ({ signal }) =>
      request(`/api/v1/agreements/${id}`, agreementDetailSchema, { signal, poll: true }),
  });

/**
 * Starting an agreement, and moving it along.
 *
 * Every one of these is a real transition on the server. The interface asks;
 * the server decides whether it is allowed and what the agreement becomes.
 */
export const stageSchema = z.object({
  id: z.string(),
  reference: z.string(),
  stage: z.string(),
});

export const launchedSchema = stageSchema.extend({ title: z.string() });

/**
 * The document as the launch form shows it.
 *
 * `placeable` says whether the markup is the kind a signature can be placed on
 * -- drawn from the document's blocks, every element saying where in them it
 * came from. A workflow uploaded before the document could be read in has
 * only its words as text, which is enough to read and not enough to point at;
 * a server that predates the question does not send it, and the answer then is
 * no.
 *
 * `document_digest` says which document the preview was drawn from, where it
 * can be placed on. The spots are sent back with it when they are saved: they
 * are only numbers, and the workflow's document can be edited in place, so
 * the server refuses spots taken on one document against another.
 *
 * `suggested` is where the document itself marks each side's signature line
 * -- the line an Ironclad signature token became when it was read in. The
 * form offers them as spots before any is placed, for the person to check and
 * confirm; a suggestion is never taken as placed on its own.
 */
export const previewSchema = z.object({
  html: z.string(),
  missing: z.array(z.string()),
  placeable: z.boolean().default(false),
  document_digest: z.string().nullable().default(null),
  suggested: z.array(placementSchema).default([]),
  found: foundSchema.default(NOTHING_FOUND),
});

export type Preview = z.infer<typeof previewSchema>;

export interface LaunchInput {
  workflow_id: string;
  counterparty_name: string;
  /**
   * As chosen on the form. Left out, a new counterparty is an organization and
   * an existing one keeps its kind; sent, an existing one takes it (`CQ-16`).
   */
  counterparty_kind?: 'person' | 'organization' | undefined;
  /** A person's email: a person is matched by name and email (`PK-04`). */
  counterparty_email?: string | undefined;
  values: Record<string, string>;
  /** Launched from a packet: the agreement becomes that step (`PKT-16`). */
  packet_id?: string | undefined;
  step_order?: number | undefined;
  /** When it expires, where the template has no end-date question (VL-2041). */
  expires_at?: string | null | undefined;
}

export const launchAgreement = (body: LaunchInput) =>
  request('/api/v1/agreements', launchedSchema, { method: 'POST', body });

/**
 * The draft's document, rendered from what has been typed so far.
 *
 * `placements` are the spots placed on the form and not saved yet --
 * signatures and the fields beside them, each saying which it is. They are
 * sent with every preview so the server draws them where the generated
 * document will have them -- the form never draws a spot of its own, and so
 * cannot show one somewhere the document would not.
 */
export const previewAgreement = (
  id: string,
  values: Record<string, string>,
  placements: readonly Placement[] = [],
) =>
  request(`/api/v1/agreements/${id}/preview`, previewSchema, {
    method: 'POST',
    body: { values, placements: placementsOut(placements) },
  });

/**
 * Say who the agreement is with, until it is sent. The title follows, and an
 * approved agreement goes back for approval.
 */
export const changeCounterparty = (
  id: string,
  name: string,
  kind?: 'person' | 'organization',
) =>
  request(`/api/v1/agreements/${id}/counterparty`, stageSchema, {
    method: 'PUT',
    body: { counterparty_name: name, ...(kind ? { counterparty_kind: kind } : {}) },
  });

/** When it expires now. */
export const expirySchema = z.object({
  id: z.string(),
  expires_at: z.string().nullable(),
});

/**
 * Set when it expires, or `null` for no expiry: its owner or an Admin, at any
 * stage (VL-2041). Not optimistic: the server decides, then the page refetches.
 */
export const setExpiry = (id: string, expiresAt: string | null) =>
  request(`/api/v1/agreements/${id}/expiry`, expirySchema, {
    method: 'PUT',
    body: { expires_at: expiresAt },
  });

/** Who signs it first now. */
export const signingOrderSchema = z.object({
  id: z.string(),
  signing_order: z.enum(['counterparty_first', 'company_first']),
  signing_order_is_own: z.boolean(),
});

/**
 * Choose who signs this agreement first, for it alone (WF-116): its owner or
 * an Admin, until it is sent. Not optimistic: the server decides, then the
 * page refetches.
 */
export const setSigningOrder = (
  id: string,
  signingOrder: 'counterparty_first' | 'company_first',
) =>
  request(`/api/v1/agreements/${id}/signing-order`, signingOrderSchema, {
    method: 'PUT',
    body: { signing_order: signingOrder },
  });

export const saveValues = (id: string, values: Record<string, string>) =>
  request(`/api/v1/agreements/${id}/values`, stageSchema, {
    method: 'PUT',
    body: { values },
  });

/** The transitions. Each is one call; the server decides what happens. */
export const generateDocument = (id: string) =>
  request(`/api/v1/agreements/${id}/generate`, stageSchema, { method: 'POST' });

export const submitAgreement = (id: string) =>
  request(`/api/v1/agreements/${id}/submit`, stageSchema, { method: 'POST' });

export const approveAgreement = (id: string, comment?: string) =>
  request(`/api/v1/agreements/${id}/approve`, stageSchema, {
    method: 'POST',
    body: { comment: comment ?? null },
  });

export const rejectAgreement = (id: string, reason: string) =>
  request(`/api/v1/agreements/${id}/reject`, stageSchema, {
    method: 'POST',
    body: { reason },
  });

/**
 * Who could be asked to approve in this approver's place.
 *
 * Only the people the server says are eligible -- active, holding a role, not
 * already approving it, not the person who started it, and not you -- so the
 * picker never offers somebody the change would then refuse. By name, fifty a
 * page; the next page starts after the last id of the one before.
 */
export const candidateSchema = z.object({
  id: z.string(),
  display_name: z.string(),
  initials: z.string(),
});

export type Candidate = z.infer<typeof candidateSchema>;

export const CANDIDATES_PER_PAGE = 50;

export const candidatesQuery = (
  agreementId: string,
  approverId: string,
  search: string,
) =>
  infiniteQueryOptions({
    queryKey: ['approver-candidates', agreementId, approverId, search] as const,
    initialPageParam: '',
    queryFn: ({ pageParam, signal }) => {
      const params = new URLSearchParams();
      if (search.trim()) params.set('q', search.trim());
      if (pageParam) params.set('cursor', pageParam);
      const query = params.toString();
      return request(
        `/api/v1/agreements/${agreementId}/approvers/${approverId}/candidates${query ? `?${query}` : ''}`,
        z.array(candidateSchema),
        { signal },
      );
    },
    getNextPageParam: (page) =>
      page.length === CANDIDATES_PER_PAGE ? page[page.length - 1]?.id : undefined,
  });

export interface ReplacementInput {
  user_id: string;
  reason: ReasonCode;
  note?: string | undefined;
}

/**
 * Ask somebody else to approve in place of an approver who has not decided.
 *
 * The server decides whether that is allowed, and answers with the approvers
 * as they now are. Asking twice is the same answer, recorded once.
 */
export const replaceApprover = (
  agreementId: string,
  approverId: string,
  body: ReplacementInput,
) =>
  request(
    `/api/v1/agreements/${agreementId}/approvers/${approverId}/replace`,
    z.array(approverSchema),
    { method: 'POST', body },
  );

export interface WithdrawalInput {
  reason: ReasonCode;
  note?: string | undefined;
}

export const withdrawalOutSchema = z.object({
  id: z.string(),
  reference: z.string(),
  stage: z.string(),
  withdrawal_pending: z.boolean(),
});

/**
 * Withdraw the signature request, so it can be corrected and sent again.
 *
 * Dropbox Sign is asked to cancel it, and Vellum waits until its own read of
 * the request says it is gone -- `withdrawal_pending` until then, which the
 * page says. Nothing moves on screen ahead of that. Everybody signs again,
 * including anybody who already had.
 */
export const withdrawAgreement = (id: string, body: WithdrawalInput) =>
  request(`/api/v1/agreements/${id}/void`, withdrawalOutSchema, { method: 'POST', body });

export const sendAgreement = (id: string) =>
  request(`/api/v1/agreements/${id}/send`, stageSchema, { method: 'POST' });

export const signAgreement = (id: string) =>
  request(`/api/v1/agreements/${id}/sign`, stageSchema, { method: 'POST' });

export const fileAgreement = (id: string) =>
  request(`/api/v1/agreements/${id}/file`, stageSchema, { method: 'POST' });

export const signatureCheckSchema = stageSchema.extend({
  last_checked_at: z.string().nullable(),
});

/**
 * Ask the signature provider now, rather than at the next scheduled check.
 *
 * The provider decides what is signed (`INT-2`); this only asks it sooner.
 * Whatever it says is recorded exactly as its callback would record it --
 * who has signed, and once everybody has, executed and filed. A provider
 * that does not answer is a 503 and nothing changes; an agreement that was
 * never sent with it is a 409, and there is nothing to ask.
 */
export const refreshSignature = (id: string) =>
  request(`/api/v1/agreements/${id}/refresh-signature`, signatureCheckSchema, {
    method: 'POST',
  });

/**
 * Who signs this one, and where they have got to.
 *
 * The workflow decides *which parties* sign and who is asked first; this says
 * *who they are*. Amida's signer is chosen when the agreement is launched and
 * the counterparty's email is typed then, because both differ every time.
 */
export const signerSchema = z.object({
  /** The row's own id: how one signer is changed once it has been sent. */
  id: z.string(),
  party: z.enum(['amida', 'counterparty']),
  name: z.string(),
  email: z.string(),
  sort_order: z.number(),
  status: z.string(),
  /**
   * Whether you may change this signer on the live request now -- by the
   * server's own rule, which it applies again when the change is asked for
   * (`API-9`).
   */
  can_change: z.boolean().default(false),
  /** Whether a change of this signer is waiting on Dropbox Sign's answer. */
  change_pending: z.boolean().default(false),
  /**
   * Whether the document prints this signer's name, which then cannot change
   * without sending it again, or their address, which the signed copy keeps.
   */
  name_printed: z.boolean().default(false),
  email_printed: z.boolean().default(false),
  /**
   * Whether the person reading may ask Dropbox Sign to remind them now, by the server's own
   * rule: the owner or an Admin, while it is their turn to sign, and not
   * inside the hour or past five today (`REM-2`, `REM-5`). For the screen
   * only; the server decides again. A server that predates the question does
   * not send it, and the answer then is no.
   */
  can_remind: z.boolean().default(false),
  /** When they were last sent again or reminded, manual or automatic (`REM-15`). */
  last_reminded_at: z.string().nullable().default(null),
  /** Who did it; null with a time means Vellum did, automatically. */
  last_reminded_by: z.string().nullable().default(null),
  /** When the next is allowed, where one is not now because of `REM-5`. */
  next_reminder_at: z.string().nullable().default(null),
  /** Whom they sign for, when out of office moved it to them; else null. */
  covering_for: z.string().nullable().default(null),
  /**
   * Which of the side's signers they are, what that signer is called in
   * Vellum, and where in the signing order they sign (templates spec §18,
   * `WF-107`). Always sent; a server that predates slots sends none, and
   * then each side has one signer, in `sort_order`.
   */
  slot: z.number().nullable().exactOptional(),
  label: z.string().nullable().exactOptional(),
  position: z.number().nullable().exactOptional(),
});

export type Signer = z.infer<typeof signerSchema>;

/**
 * One change of signer after sending, and where it got to. Names, never an
 * address; the note is shown here and goes nowhere else.
 */
export const signerChangeSchema = z.object({
  id: z.string(),
  party: z.enum(['amida', 'counterparty']),
  replaced_name: z.string(),
  name: z.string(),
  reason: z.enum(REASON_CODES),
  note: z.string().nullable(),
  state: z.enum(['requested', 'applied', 'refused', 'failed']),
  changed_by_name: z.string(),
  requested_at: z.string(),
  settled_at: z.string().nullable(),
  domain_changed: z.boolean(),
  printed_email_kept: z.boolean(),
});

export type SignerChange = z.infer<typeof signerChangeSchema>;

export const signerChangesQuery = (id: string) =>
  queryOptions({
    queryKey: ['signer-changes', id] as const,
    queryFn: ({ signal }) =>
      request(`/api/v1/agreements/${id}/signers/changes`, z.array(signerChangeSchema), {
        signal,
      }),
  });

export type SignerChangeInput =
  | {
      party: 'amida';
      user_id: string;
      reason: ReasonCode;
      note?: string | undefined;
    }
  | {
      party: 'counterparty';
      name: string;
      email: string;
      reason: ReasonCode;
      note?: string | undefined;
      confirm_domain_change?: boolean | undefined;
    };

/**
 * Change one signer who has not signed, on the request already sent.
 *
 * Dropbox Sign makes the change and Vellum records only its answer, so this
 * waits for it rather than updating the screen ahead of it. The answer is the
 * signers as they now are; a change whose answer did not arrive comes back
 * with `change_pending` on that signer, and Vellum reads the request to
 * settle it -- it is never asked for twice.
 */
export const replaceSigner = (
  agreementId: string,
  signerId: string,
  body: SignerChangeInput,
) =>
  request(
    `/api/v1/agreements/${agreementId}/signers/${signerId}/replace`,
    z.array(signerSchema),
    { method: 'POST', body },
  );

export interface SignerInput {
  party: 'amida' | 'counterparty';
  name: string;
  email: string;
  /**
   * For the counterparty: that a move from the address already named to
   * another domain was seen and meant. The server refuses the move without it.
   */
  confirm_domain_change?: boolean | undefined;
  /**
   * Which of the side's signers this person is, from one (templates spec
   * §18). Left out, 1. A number past the template's adds a signer, after the
   * template's (`WF-101`).
   */
  slot?: number | undefined;
}

export const signersQuery = (id: string) =>
  queryOptions({
    queryKey: ['signers', id] as const,
    // A poll, for the same reason as `agreementQuery`.
    queryFn: ({ signal }) =>
      request(`/api/v1/agreements/${id}/signers`, z.array(signerSchema), {
        signal,
        poll: true,
      }),
  });

/**
 * Say who signs. Replaces the list, before it is sent.
 *
 * For the person who started it, or an administrator. Refused once the
 * agreement has been sent: from then on one signer who has not signed is
 * changed at a time, at Dropbox Sign (`replaceSigner`).
 */
export const setSigners = (id: string, signers: SignerInput[]) =>
  request(`/api/v1/agreements/${id}/signers`, z.array(signerSchema), {
    method: 'PUT',
    body: signers,
  });

export const signaturePlacementsSchema = z.object({
  placements: z.array(placementSchema),
});

/**
 * Say where each side signs, and what they fill in beside it. Replaces what
 * was there.
 *
 * At least one signature and at most one a party, with up to eight fields for
 * each side that signs and twenty spots in all. Dropbox Sign puts its boxes at
 * these characters and nowhere else, so the server checks each is on the
 * document's own words -- not in a blank, not on a signature block -- and
 * refuses the lot rather than keep the half that fitted. Refused, too, once
 * the agreement has been sent: the envelope was built from the document these
 * made.
 *
 * `documentDigest` is the `document_digest` of the preview the spots were
 * placed on. If the workflow's document has been edited since, the same
 * numbers would point at different words, and the server refuses them.
 */
export const setSignaturePlacements = (
  id: string,
  placements: readonly Placement[],
  documentDigest: string,
) =>
  request(`/api/v1/agreements/${id}/signature-placements`, signaturePlacementsSchema, {
    method: 'PUT',
    body: { placements: placementsOut(placements), document_digest: documentDigest },
  });

/**
 * Resending and reminding (`reminders/spec.md`, `REM-3` to `REM-13`).
 *
 * Each is one call; the server decides whether it is allowed -- who may, and
 * not more than one an hour or five a day per person (`REM-5`) -- and refuses
 * with a `429` saying when the next may go. The screen only offers what
 * `can_remind` says.
 */
export const reminderSchema = z.object({
  person_id: z.string(),
  name: z.string(),
  reminded_at: z.string(),
  reminded_by: z.string(),
  next_reminder_at: z.string(),
  /**
   * Whether the email went. False: the notice is in Vellum, and the email
   * did not go. A signer's is always true -- Dropbox Sign sends it.
   */
  delivered: z.boolean(),
});

export type Reminder = z.infer<typeof reminderSchema>;

export const SKIP_REASONS = [
  'too_soon',
  'daily_limit',
  'provider_refused',
  'provider_unavailable',
] as const;

export type SkipReason = (typeof SKIP_REASONS)[number];

/** Everybody reminded in one go, and who was skipped and why (`REM-13`). */
export const reminderBatchSchema = z.object({
  reminded: z.number(),
  skipped: z.number(),
  skipped_by_reason: z.array(
    z.object({ reason: z.enum(SKIP_REASONS), count: z.number() }),
  ),
  delivered: z.boolean(),
});

export type ReminderBatch = z.infer<typeof reminderBatchSchema>;

/** Send one approver the approval request again, marked *Sent again* (`REM-3`). */
export const resendToApprover = (agreementId: string, approverId: string) =>
  request(
    `/api/v1/agreements/${agreementId}/approvers/${approverId}/resend`,
    reminderSchema,
    { method: 'POST' },
  );

/** Send the request again to every approver who has not decided (`REM-3`). */
export const resendToEveryone = (agreementId: string) =>
  request(`/api/v1/agreements/${agreementId}/approvers/resend`, reminderBatchSchema, {
    method: 'POST',
  });

/** Send one approver a reminder (`REM-4`). */
export const remindApprover = (agreementId: string, approverId: string) =>
  request(
    `/api/v1/agreements/${agreementId}/approvers/${approverId}/remind`,
    reminderSchema,
    { method: 'POST' },
  );

/**
 * Ask Dropbox Sign to email a signer the signing link again (`REM-8`).
 * Nothing about the signature changes (`INT-2`).
 */
export const remindSigner = (agreementId: string, signerId: string) =>
  request(
    `/api/v1/agreements/${agreementId}/signers/${signerId}/remind`,
    reminderSchema,
    { method: 'POST' },
  );

/** The shortest and longest reason the server accepts, after trimming (`LIFE-65`). */
export const CANCEL_REASON_LEAST = REASON_LEAST;
export const CANCEL_REASON_MOST = REASON_MOST;

/**
 * Whether a reason may be sent, by the server's own rule (`LIFE-65`,
 * ADW-5501): long enough, short enough, and no refused character.
 */
export function reasonGiven(reason: string): boolean {
  return reasonProblem(reason) === null;
}

/**
 * Cancel an agreement, saying why (`LIFE-64`..`LIFE-66`).
 *
 * The answer is the agreement as it now stands: `cancelled`, or -- out for
 * signature -- still there with `cancel_pending`, until Dropbox Sign confirms
 * its request is withdrawn. The reason is shown on its page and goes nowhere
 * else.
 */
/** What a cancel answers: where the agreement now stands, as the other actions do. */
export const cancelledSchema = z.object({
  id: z.string(),
  reference: z.string(),
  stage: z.string(),
  /** Out for signature: withdrawn first, cancelled once Dropbox Sign confirms. */
  cancel_pending: z.boolean().default(false),
});

export const cancelAgreement = (id: string, reason: string) =>
  request(`/api/v1/agreements/${id}/cancel`, cancelledSchema, {
    method: 'POST',
    body: { reason: reason.trim() },
  });

/** What a copy answers: how many addresses took it, and how many did not. */
export const copySentSchema = z.object({ sent: z.number(), failed: z.number() });

export type CopySent = z.infer<typeof copySentSchema>;

/** A copy as the dialog sends it: addresses, a subject and a plain-text message. */
export type CopyRequest = { to: string[]; subject: string; body: string };

/**
 * Email a copy of a filed agreement's signed document (`LIFE-70`, ADR-0008).
 * A person presses Send; nothing sends one by itself.
 */
export const sendAgreementCopy = (id: string, body: CopyRequest) =>
  request(`/api/v1/agreements/${id}/copy`, copySentSchema, { method: 'POST', body });

export const deletedAgreementSchema = z.object({
  id: z.string(),
  reference: z.string(),
  documents: z.number(),
  /**
   * Whether it was sent for signature before it was cancelled, and so kept on
   * record rather than deleted outright. Either way it no longer appears in
   * Vellum; the screen calls both **Delete**.
   */
  removed: z.boolean(),
});

/**
 * Delete an agreement: an Admin's, anybody's never sent or cancelled; the
 * person who started it, their own once it is cancelled.
 *
 * One never sent goes with its drafts. One sent and then cancelled no longer
 * appears anywhere in Vellum and is kept on record, since the signature
 * provider keeps one too. Anything out for signature or signed the server
 * refuses, saying why, and the record that this one existed survives it
 * either way.
 */
export const deleteAgreement = (id: string) =>
  request(`/api/v1/agreements/${id}`, deletedAgreementSchema, { method: 'DELETE' });

/** What has happened to an agreement. */
export const activitySchema = z.object({
  /** The entry's place in the trail: the cursor for the page after it. */
  id: z.number().int().positive(),
  occurred_at: z.string(),
  action: z.string(),
  outcome: z.string(),
  actor_name: z.string().nullable(),
  actor_initials: z.string().nullable(),
  detail: z.record(z.string(), z.unknown()).nullable(),
  /** Whom the action was taken for: the person away, when out of office acted. */
  on_behalf_of_name: z.string().nullable().default(null),
});

export type Activity = z.infer<typeof activitySchema>;

/**
 * Why Dropbox Sign could not keep a signature request, by the code the server
 * records on its `signature_request_failed` row (VL-2030). The server keeps a
 * code and never the provider's words; these are what the page says for each.
 * A code not here keeps the general line in `ACTION_LABEL`.
 */
export const SIGNATURE_FAILURE_LABEL: Record<string, string> = {
  text_tag:
    'found Dropbox Sign could not read a signature spot in the document — usually one with a space inside its label, or typed into the template by hand. Place each spot again in the template and send again',
  field_position:
    'found Dropbox Sign could not place a signature box where the document puts it. Move the signature spots in the template and send again',
  file: 'found Dropbox Sign could not open the document. Make the document again and send it again',
  expired:
    'found the signature request expired at Dropbox Sign before everybody signed, so it came back to be sent again',
  unknown:
    'found Dropbox Sign could not process the signature request, so it came back to be sent again',
};

/** How each action reads in the feed, in the person's own words. */
export const ACTION_LABEL: Record<string, string> = {
  created: 'started this agreement',
  generated: 'generated the document',
  details_changed: 'changed the details',
  counterparty_changed: 'changed who it is with',
  signatures_placed: 'said where it is signed',
  submitted: 'sent it for approval',
  approved: 'approved it',
  rejected: 'sent it back',
  approver_replaced: 'asked somebody else to approve in an approver’s place',
  signers_named: 'said who signs',
  signer_change_requested: 'asked Dropbox Sign for a different signer',
  signer_changed: 'recorded a different signer, as Dropbox Sign confirmed',
  signer_change_refused: 'found the change of signer refused, so nothing changed',
  signer_change_failed: 'found the change of signer was not made',
  signer_change_unconfirmed: 'is waiting on Dropbox Sign to confirm a change of signer',
  // One signer's signature, seen for the first time. Kept whatever becomes
  // of the request it was on.
  signer_signed: 'recorded a signature, as Dropbox Sign reported it',
  withdrawal_requested: 'asked Dropbox Sign to withdraw the signature request',
  signature_cancel_asked: 'asked Dropbox Sign to cancel the signature request',
  signature_request_withdrawn:
    'found the signature request withdrawn, so it can be sent again',
  withdrawal_refused: 'found Dropbox Sign would not withdraw it, so nothing changed',
  withdrawal_overtaken: 'found everybody had signed before it could be withdrawn',
  withdrawal_failed: 'found the withdrawal was not made, so nothing changed',
  // A request that ended with nobody's signature sends it back to its owner
  // to fix and send again (SG-03, SG-04). Its reason is on this row.
  declined: 'found the signature request declined, so it came back to be sent again',
  signature_request_failed:
    'found Dropbox Sign could not process the signature request, or it had expired, so it came back to be sent again',
  // The address bounced; the way on is to change the signer (SG-05).
  signer_bounced: "found a signer's email bounced",
  // Its values or its document changed after somebody approved it, so it
  // went back for approval: what was approved is what is sent.
  approvals_cleared: 'changed it after approval, so it went back to draft',
  // A one-off repair: a rejection had been left standing through a resubmit.
  approval_round_reopened: 'reopened the approval round, so everyone is asked again',
  rejection_bypassed_before_fix:
    'found it had been sent past a rejection, before that was fixed',
  sent: 'sent it for signature',
  signed: 'recorded the signature',
  filed: 'filed it',
  // Signed somewhere else and brought in: from a spreadsheet, or as the
  // signed PDF itself. No signature is recorded, because Vellum saw none.
  imported: 'imported it, signed outside Vellum',
  'agreement.imported': 'imported it, signed outside Vellum',
  // Filing's second half: the drafts leave the working store once the
  // signed agreement is filed (ADR-0005).
  drafts_removed: 'cleared the drafts once it was filed',
  // The provider keeps refusing to say anything about the request -- it was
  // cancelled there, or made under an old key. Somebody should look.
  signature_request_refused: 'found the signature provider no longer knows this request',
  superseded: 'superseded it',
  // Never with the reason: that is on the page alone (`LIFE-67`).
  cancel_requested: 'asked to cancel it, once Dropbox Sign withdraws the request',
  cancelled: 'cancelled it',
  // Resending and reminding (`REM-14`). With nobody named, Vellum did it on
  // its own -- the Who column then says "Vellum".
  'agreement.approval_resent': 'sent the approval request again',
  'agreement.approver_reminded': 'reminded an approver',
  'agreement.signer_reminded': 'asked Dropbox Sign to remind a signer',
  'agreement.send_reminded': 'reminded the owner it is ready to send',
  // Transfer ownership was withdrawn on 22 October 2026; what it did stays
  // on the record, and still reads as what it was.
  'agreement.owner_changed': 'moved it to a new owner',
  // A person emailed the signed copy (`PKT-23`, `LIFE-72`). The trail holds
  // how many and their domains, never who or what was written.
  shared: 'emailed a copy of the signed document',
  // Somebody pressed Download and the filed copy was gone from the store (SG-12).
  filed_copy_missing: 'found the signed copy missing from the document store',
  // A filed agreement is amended by a new one, never changed (SG-17).
  amendment_started: 'started this as an amendment',
  amended: 'started an amendment to it',
  // When it expires is its record, not its document (VL-2041).
  'agreement.expiry_set': 'set when it expires',
  'agreement.signing_order_changed': 'changed who signs first',
  // An agreement on the counterparty's paper: their file, as it came, and a
  // corrected one before it was sent -- each a version of its own, never an
  // edit to the one before.
  document_uploaded: 'uploaded the counterparty’s document',
  document_replaced: 'replaced the counterparty’s document',
};

/**
 * How an action reads when it did not complete, where that is a different
 * sentence rather than the same one marked "Did not complete".
 */
export const ACTION_FAILED_LABEL: Record<string, string> = {
  'agreement.signer_reminded': 'found Dropbox Sign would not send a reminder',
  shared: 'tried to email a copy of the signed document, and it was not sent',
};

/**
 * Rows the trail keeps for its own bookkeeping, not for people to read. A
 * signer's reminder is recorded as asked before Dropbox Sign is, so a second
 * press waits; the answer that follows is the row that says what happened,
 * and showing both would read as two reminders.
 */
const HIDDEN_ACTIONS = new Set(['agreement.signer_reminder_asked']);

/**
 * Why out of office left an item where it was, by the code the audit row
 * carries (`OOO-7`). Codes only reach the trail; the words are chosen here.
 */
const OUT_OF_OFFICE_REFUSAL: Record<string, string> = {
  colleague_owns: 'the colleague covering started it',
  already_approving: 'the colleague covering already approves it',
  rejected_last_round: 'the colleague covering rejected it last time round',
  name_printed: 'the document prints the signer’s name',
  counterparty_signer: 'the colleague covering signs it for the other side',
  // Several signers a side (templates spec §18, `WF-110`): the slot stays.
  already_signing: 'the colleague covering already signs it in another place',
  person_inactive: 'the person away no longer has an active account',
  provider_refused: 'Dropbox Sign would not change the signer',
};

/**
 * An out-of-office row of the activity, in words (`OOO-22`): what moved,
 * which way, and -- where it could not -- why. Its detail holds codes only;
 * whom it was for is the row's `on_behalf_of_name`, added by the page.
 */
export function outOfOfficeAction(entry: Activity): string {
  const detail = entry.detail ?? {};
  const what = detail.kind === 'signature' ? 'the signature' : 'the approval';
  const back = detail.direction === 'back';
  if (entry.action === 'out_of_office.moved') {
    return `moved ${what} to the colleague covering while out of office`;
  }
  if (entry.action === 'out_of_office.returned') {
    return `handed ${what} back after out of office`;
  }
  if (entry.action === 'out_of_office.refused') {
    const code = typeof detail.reason === 'string' ? detail.reason : '';
    const why = OUT_OF_OFFICE_REFUSAL[code];
    const verb = back ? `could not hand ${what} back` : `could not move ${what}`;
    return why ? `${verb}: ${why}` : verb;
  }
  return ACTION_LABEL[entry.action] ?? entry.action;
}

/** How many entries the server sends on a page of activity, unless asked. */
export const ACTIVITY_PER_PAGE = 50;

/** One page of an agreement's activity, and where the next one starts. */
export interface ActivityPage {
  entries: Activity[];
  /** The cursor for the page after this one; absent on the last. */
  next: number | undefined;
}

/**
 * An agreement's activity, newest first, a page at a time (`CMP-05`).
 *
 * The cursor is the last entry's id, read before the hidden actions are
 * dropped, so a page made only of hidden entries still leads to the next. A
 * page shorter than a full one is the last. The first page asks with no query
 * at all: the server's own default is the page size.
 */
export const activityQuery = (id: string) =>
  infiniteQueryOptions({
    queryKey: ['activity', id] as const,
    initialPageParam: undefined as number | undefined,
    queryFn: async ({ pageParam, signal }): Promise<ActivityPage> => {
      const query = pageParam === undefined ? '' : `?cursor=${String(pageParam)}`;
      const raw = await request(
        `/api/v1/agreements/${id}/activity${query}`,
        z.array(activitySchema),
        { signal },
      );
      return {
        entries: raw.filter((entry) => !HIDDEN_ACTIONS.has(entry.action)),
        next: raw.length === ACTIVITY_PER_PAGE ? raw[raw.length - 1]?.id : undefined,
      };
    },
    getNextPageParam: (page) => page.next,
  });

/** Where the signed PDF is read for Vellum's own viewer (`LIFE-74`). */
export const viewDocumentPath = (id: string): string =>
  `/api/v1/agreements/${id}/document/view`;

/**
 * The signed PDF, for the viewer to draw. Brokered through Vellum's own
 * permission check, same-origin with the session cookie, and never a store's
 * link (`DOC-6`). The bytes are kept in memory by the viewer alone and let go
 * when it closes; the server marks them `no-store`, so no cache keeps them.
 */
export const viewDocument = (id: string, signal?: AbortSignal): Promise<ArrayBuffer> =>
  requestFile(viewDocumentPath(id), signal ? { signal } : {});
